import { create } from 'zustand';
import { deviceApi } from '../api/device.api';
import { Device } from '../gen/proto/api/gateway/v1/gateway_pb';

type DeviceStoreEntity = { [key: string]: Device };

interface DeviceStore {
  entities: DeviceStoreEntity;
  totalEntities: number;

  create: (name: string, type: string) => Promise<Device>;
  getByUuid: (uuid: string) => Promise<Device>;
  getListByPagination: (page: number, interval: number) => Promise<Device[]>;
  delete: (uuid: string) => void;
  update: (device: Device) => void;
}

export const useDeviceStore = create<DeviceStore>()((set, get) => ({
  entities: {},
  totalEntities: 0,
  create: async function (name: string, type: string): Promise<Device> {
    try {
      const device = await deviceApi.create(name, type);
      set((state) => {
        return {
          entities: { ...state.entities, ...{ [device.uuid]: device } },
          totalEntities: state.totalEntities + 1,
        };
      });
      return device;
    } catch (error) {
      throw error;
    }
  },
  getByUuid: async function (uuid: string): Promise<Device> {
    try {
      const device = await deviceApi.getByUuid(uuid);
      set((state) => {
        return {
          entities: { ...state.entities, ...{ [device.uuid]: device } },
        };
      });
      return device;
    } catch (error) {
      throw error;
    }
  },
  getListByPagination: async (page: number, interval: number) => {
    try {
      const data = await deviceApi.getListByPagination(page, interval);
      if (data.data == null) return [];

      const currentDevices = get().entities;
      const devices = data.data;

      devices.forEach((device) => {
        currentDevices[device.uuid] = device;
      });

      set({
        entities: currentDevices,
        totalEntities: data.metadata?.totalCount || 0,
      });
      return data.data;
    } catch (error) {
      throw error;
    }
  },
  delete: async function (uuid: string) {
    const response = await deviceApi.delete(uuid);
    if (response) {
      set((state) => {
        delete state.entities[uuid];
        return {
          entities: { ...state.entities },
          totalEntities: state.totalEntities - 1,
        };
      });
    }
  },
  update: async function (device: Device) {
    const updateDevice = await deviceApi.update(device);
    set((state) => {
      return {
        entities: {
          ...state.entities,
          ...{ [updateDevice.uuid]: updateDevice },
        },
        totalEntities: state.totalEntities + 1,
      };
    });
  },
}));

export default useDeviceStore;
